import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import workbenchImage from "@/assets/images/sqithome.png";
import { useState } from "react";
import { useCmsPage } from "@/hooks/useCms";
import ContactInfo from "@/components/cms/ContactInfo";
import ContactForm from "@/components/cms/ContactForm";
import useContactForm from "@/hooks/useContactForm";
import { ArrowRight } from "lucide-react";

import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

export default function SoftwareHero() {

    const [open, setOpen] = useState(false);

    const { page, loading } = useCmsPage("contact");

    const {
        form,
        change,
        submit,
        submitting,
        done,
    } = useContactForm();

    const modules = [
        {
            name: "Raw Data QC",
            description: "Quality assessment and preprocessing of raw sequencing reads.",
            sampleReport: "raw-data-qc",
        },
        {
            name: "Variant Analysis",
            description: "Identify, annotate, and prioritize genomic variants.",
            sampleReport: "variant-analysis",
        },
        {
            name: "Exome/Targeted Sequencing",
            description: "Comprehensive analysis of protein-coding regions.",
            sampleReport: "exome",
        },
        {
            name: "Transcriptome Resequencing",
            description: "Gene expression and transcript quantification analysis.",
            sampleReport: "transcriptome-model",
        },
        {
            name: "Metagenome",
            description: "Taxonomic and functional profiling of microbial communities.",
            sampleReport: "metagenome",
        },
        {
            name: "Epigenome Profiling (ChIP-Seq)",
            description: "Genome-wide protein-DNA interaction and peak analysis.",
            sampleReport: "chipseq",
        },
    ];

    return (
        <div className="relative w-full min-h-48 bg-gradient-to-br from-[#F8FAF7] via-white to-[#F3F8EF]">
            {/* Background */}
            <div className="absolute inset-0 hero-grid opacity-50" />
            <div className="absolute -top-40 -left-40 h-96 w-96 rounded-full bg-[#EAF4E3] blur-3xl opacity-70" />
            <div className="absolute -bottom-52 -right-52 h-[32rem] w-[32rem] rounded-full bg-[#DDEED2] blur-3xl opacity-60" />

            <div className="relative z-10 flex min-h-full flex-col px-10 py-10">

                <Link to="/" className="w-fit">
                    <Logo brand="saas" />
                </Link>

                <div className="w-full">

                    <h2 className="mt-4 text-5xl font-display font-bold leading-tight text-gray-900">
                        Your Omics
                        <span className="text-[#0B6936]"> Cloud Platform.</span>
                    </h2>

                    <p className="mt-4 text-lg leading-8 text-gray-600 w-full">
                        SQIT.ai™ Software is an enterprise bioinformatics platform that enables researchers
                        to perform end-to-end NGS data analysis using integrated workflows, automated
                        pipelines, and publication-ready reporting—all within a single graphical interface.
                    </p>

                    <div className="mt-8">
                        <button
                            type="button"
                            onClick={() => setOpen(true)}
                            className="group relative w-full cursor-zoom-in outline-none"
                        >
                            <img
                                src={workbenchImage}
                                alt="SQIT Workbench"
                                className="w-full max-h-[300px] object-contain transition duration-300 group-hover:scale-[1.02]"
                            />

                            <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/0 transition group-hover:bg-black/20">
                                <span className="rounded-full bg-white/90 px-4 py-2 text-sm font-medium opacity-0 shadow transition group-hover:opacity-100">
                                    View Full Interface
                                </span>
                            </div>
                        </button>
                    </div>

                   
                    {/* Inaugural Offer */}
                    <div className="mt-6 overflow-hidden rounded-2xl border border-[#0B6936]/20 bg-gradient-to-r from-[#F7FBF4] via-white to-[#F7FBF4] shadow-sm">

                        {/* Top Badge */}
                        <div className="flex items-center justify-center bg-[#0B6936] px-4 py-2">
                            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white">
                                🎉 Launch Offer
                            </span>
                        </div>

                        <div className="p-6">

                            <div className="flex flex-wrap items-center gap-3">
                                <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-red-600">
                                    Save 80%
                                </span>

                                <span className="text-lg text-gray-400 line-through">
                                    ₹2,500 (5 Credits)
                                </span>
                            </div>

                            <h3 className="mt-4 text-3xl font-bold text-gray-900">
                                Analyze any sample for
                                <span className="ml-3 text-4xl font-bold text-[#0B6936]">
                                    ₹500
                                    <span className="ml-2 text-2xl font-semibold">
                                        (1 Credit)
                                    </span>
                                </span>
                            </h3>

                            <p className="mt-3 text-gray-600">
                                Pay only for the analyses you run. No subscription. No hidden charges.
                            </p>

                            <div className="mt-5 flex flex-wrap gap-3">

                                <span className="rounded-full bg-[#0B6936]/10 px-3 py-2 text-sm font-medium text-[#0B6936]">
                                    ✓ Pay-as-you-go
                                </span>

                                <span className="rounded-full bg-[#0B6936]/10 px-3 py-2 text-sm font-medium text-[#0B6936]">
                                    ✓ Enterprise Pipelines
                                </span>

                                <span className="rounded-full bg-[#0B6936]/10 px-3 py-2 text-sm font-medium text-[#0B6936]">
                                    ✓ Publication-ready Reports
                                </span>

                            </div>

                        </div>

                    </div>

                    <div className="mt-auto pt-6">
                        <Accordion type="multiple" className="w-full space-y-3">

                            <AccordionItem
                                id="available-modules"
                                value="modules"
                                className="rounded-xl border border-[#D9E8CC] bg-white px-5"
                            >
                                <AccordionTrigger className="text-xl font-semibold text-[#0B6936] hover:no-underline">
                                    Available Modules
                                </AccordionTrigger>

                                <AccordionContent>
                                    <div className="grid grid-cols-1 gap-3 pt-2">
                                        {modules.map((module) => (
                                            <div
                                                key={module.name}
                                                className="rounded-lg border border-[#D9E8CC] bg-[#F7FBF4] p-4 transition hover:border-[#538135]"
                                            >
                                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                                    <div className="flex-1">
                                                        <h4 className="text-sm font-semibold text-gray-900">
                                                            {module.name}
                                                        </h4>

                                                        <p className="mt-1 text-sm text-gray-600">
                                                            {module.description}
                                                        </p>
                                                    </div>

                                                    <a
                                                        href={`/sample-reports/${module.sampleReport}.html`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex w-fit items-center gap-2 rounded-md border border-[#0B6936] px-3 py-2 text-xs font-semibold text-[#0B6936] transition hover:bg-[#0B6936] hover:text-white sm:shrink-0"
                                                    >
                                                        View Report
                                                        <ArrowRight size={14} />
                                                    </a>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </AccordionContent>
                            </AccordionItem>

                            <AccordionItem
                                id="contact"
                                value="support"
                                className="rounded-xl border border-[#D9E8CC] bg-white px-5"
                            >
                                <AccordionTrigger className="text-lg font-semibold text-[#0B6936] hover:no-underline">
                                    Contact Us
                                </AccordionTrigger>

                                <AccordionContent className="pt-6">
                                    {!loading && page && (
                                        <>
                                            {done ? (
                                                <div className="rounded-xl border border-[#D9E8CC] bg-[#F7FBF4] p-6 text-center">
                                                    <h3 className="text-lg font-semibold text-[#538135]">
                                                        Message Sent Successfully
                                                    </h3>

                                                    <p className="mt-2 text-sm text-gray-600">
                                                        We'll respond within 1 business day.
                                                    </p>
                                                </div>
                                            ) : (
                                                <>
                                                    <ContactInfo page={page} compact />

                                                    <div className="mt-6 border-t border-[#E5E7EB] pt-6">
                                                        <ContactForm
                                                            page={page}
                                                            form={form}
                                                            change={change}
                                                            submit={submit}
                                                            submitting={submitting}
                                                            done={done}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                </AccordionContent>
                            </AccordionItem>

                        </Accordion>
                    </div>

                </div>
            </div>
            {open && (
                <div
                    className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-8 animate-fadeIn"
                    onClick={() => setOpen(false)}
                >
                    <button
                        className="absolute top-6 right-8 text-white text-5xl leading-none"
                        onClick={() => setOpen(false)}
                    >
                        ×
                    </button>

                    <img
                        src={workbenchImage}
                        alt="SQIT Workbench"
                        className="max-w-[95vw] max-h-[92vh] rounded-xl shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
}